import Button from '@/components/ui/Button/Button';
import Icon from '@/components/ui/Icon/Icon';
import type { StatusKind } from '@/components/ui/StatusRow/StatusRow';
import type { Point, SiteAnalysisResponse } from '@/lib/api.types';
import { CHAPTERS } from '@/lib/chapters';
import { formatLatLng, splitAddress } from '@/lib/format';
import { rankToStatus, rankToStatusLabel } from '@/lib/risk';
import { buildVerdict, STATUS_SYMBOL } from '@/lib/verdict';
import { useStepCycle } from '@/hooks/useStepCycle';
import styles from './SiteBrief.module.css';

// 地図の隣に置く「概要カード」。Shirabe の状態に応じて中身が変わる機能カード。
//  idle      … まだ地点なし。何が分かり/何の代わりでないかを“教える”空状態。
//  located   … 地点確定・未解析。住所＋分かること＋「調査を開始」CTA をカード内に内包。
//  analyzing … 取得中。同じ位置でインライン進捗を見せる（全画面演出と二重にしない）。
//  ready     … 解析済み。VerdictCard と同じ buildVerdict による結論を先出し＋本文へ誘導。
export type BriefView = 'idle' | 'located' | 'analyzing' | 'ready';

type Props = {
  view: BriefView;
  address?: string | null;
  point?: Point | null;
  analysis?: SiteAnalysisResponse | null;
  /** analyzing 中に巡回表示するフェーズ文言。 */
  steps?: string[];
  /** 成果物名。idle の見出しに出す。省略時は「立地レポート」。 */
  reportTitle?: string;
  onStart?: () => void;
  onSeeDetails?: () => void;
};

const TINT: Record<StatusKind, string> = {
  safe: styles.safe,
  alert: styles.alert,
  neutral: styles.neutral,
};

// 調べる8つの観点（章）。idle/located でこの土地で“分かること”として見せる。
function TopicGrid() {
  return (
    <ul className={styles.topics}>
      {CHAPTERS.map((c) => (
        <li key={c.id} className={styles.topic}>
          <Icon name={c.icon} size={15} />
          <span>{c.shortTitle}</span>
        </li>
      ))}
    </ul>
  );
}

function LocationLine({
  address,
  point,
}: {
  address?: string | null;
  point?: Point | null;
}) {
  const { pref, name } = splitAddress(address);
  const main =
    name || pref || (point ? formatLatLng(point.lat, point.lon) : '地点未選択');
  return (
    <div className={styles.loc}>
      <Icon name="map-pin" size={16} />
      <div className={styles.locText}>
        {pref && name && <span className={styles.locPref}>{pref}</span>}
        <span className={styles.locName}>{main}</span>
      </div>
    </div>
  );
}

function Trust() {
  return (
    <div className={styles.trust}>
      <p className={styles.sources}>
        出典：国土地理院・不動産情報ライブラリ・J-SHIS ほかの公的データ
      </p>
      <p className={styles.disclaimer}>
        ※このレポートは重要事項説明や専門家の判断に代わるものではありません。
      </p>
    </div>
  );
}

function BriefIdle({ reportTitle = '立地レポート' }: { reportTitle?: string }) {
  return (
    <>
      <div className={styles.eyebrow}>{reportTitle}</div>
      <h2 className={styles.pitch}>
        この土地、買って大丈夫？を
        <br />
        まとめて調べます。
      </h2>
      <ol className={styles.steps}>
        <li>
          <span className={styles.stepNum}>1</span>
          住所を入れる、または地図をクリック
        </li>
        <li>
          <span className={styles.stepNum}>2</span>
          「この地点で調査を開始」を押す
        </li>
        <li>
          <span className={styles.stepNum}>3</span>
          <span>
            ◯ / △ の結論と、くわしい
            <strong>8 つの章</strong>が出ます
          </span>
        </li>
      </ol>
      <div className={styles.topicBlock}>
        <div className={styles.blockLabel}>わかること</div>
        <TopicGrid />
      </div>
      <Trust />
    </>
  );
}

function BriefLocated({
  address,
  point,
  onStart,
}: {
  address?: string | null;
  point?: Point | null;
  onStart?: () => void;
}) {
  return (
    <>
      <div className={styles.eyebrow}>この地点を調べます</div>
      <LocationLine address={address} point={point} />
      <div className={styles.topicBlock}>
        <div className={styles.blockLabel}>この地点でわかること</div>
        <TopicGrid />
      </div>
      <Button
        variant="cta"
        size="lg"
        className={`${styles.fullBtn} ${styles.startBtn}`}
        onClick={onStart}
      >
        この地点で調査を開始
      </Button>
      <p className={styles.hint}>
        公的データをまとめて取得します。レポートが出るまで数秒〜十数秒ほどかかります。
      </p>
      <Trust />
    </>
  );
}

function BriefAnalyzing({
  address,
  point,
  steps,
}: {
  address?: string | null;
  point?: Point | null;
  steps?: string[];
}) {
  const list = steps && steps.length > 0 ? steps : ['調査しています…'];
  const idx = useStepCycle(true, list.length);
  return (
    <>
      <div className={styles.eyebrow}>調査中です</div>
      <LocationLine address={address} point={point} />
      <div className={styles.progressHead}>
        <span className={styles.spinner} aria-hidden="true" />
        <p className={styles.progressNow} role="status" aria-live="polite">
          {list[idx]}
        </p>
      </div>
      <ol className={styles.progressList}>
        {list.map((s, i) => {
          const state =
            i < idx ? styles.done : i === idx ? styles.current : '';
          return (
            <li key={i} className={`${styles.progressItem} ${state}`}>
              <span className={styles.progressMark} aria-hidden="true">
                {i < idx ? '✓' : i === idx ? '…' : '·'}
              </span>
              {s}
            </li>
          );
        })}
      </ol>
    </>
  );
}

function BriefReady({
  analysis,
  address,
  point,
  onSeeDetails,
}: {
  analysis: SiteAnalysisResponse;
  address?: string | null;
  point?: Point | null;
  onSeeDetails?: () => void;
}) {
  const v = buildVerdict(analysis);
  return (
    <>
      <div className={styles.eyebrow}>この土地のレポート</div>
      <LocationLine address={address ?? analysis.address} point={point} />
      <div className={`${styles.badge} ${TINT[v.status]}`}>
        <span className={styles.badgeSymbol} aria-hidden="true">
          {STATUS_SYMBOL[v.status]}
        </span>
        {v.statusLabel}
      </div>
      <p className={styles.readyMsg}>{v.message}</p>
      <ul className={styles.dims}>
        {v.dims.map((d) => {
          const s = rankToStatus(d.rank);
          return (
            <li key={d.key} className={`${styles.dim} ${TINT[s]}`}>
              <span className={styles.dimSymbol} aria-hidden="true">
                {STATUS_SYMBOL[s]}
              </span>
              <span className={styles.dimLabel}>{d.label}</span>
              <span className={styles.dimRank}>
                {rankToStatusLabel(d.rank, '—')}
              </span>
            </li>
          );
        })}
      </ul>
      <Button
        variant="cta"
        size="lg"
        className={styles.fullBtn}
        onClick={onSeeDetails}
      >
        レポートをくわしく見る
      </Button>
      <p className={styles.disclaimer}>
        ※このレポートは重要事項説明や専門家の判断に代わるものではありません。
      </p>
    </>
  );
}

export default function SiteBrief({
  view,
  address,
  point,
  analysis,
  steps,
  reportTitle = '立地レポート',
  onStart,
  onSeeDetails,
}: Props) {
  return (
    <div id="brief" className={styles.card} aria-label={reportTitle}>
      {view === 'idle' && <BriefIdle reportTitle={reportTitle} />}
      {view === 'located' && (
        <BriefLocated address={address} point={point} onStart={onStart} />
      )}
      {view === 'analyzing' && (
        <BriefAnalyzing address={address} point={point} steps={steps} />
      )}
      {view === 'ready' && analysis && (
        <BriefReady
          analysis={analysis}
          address={address}
          point={point}
          onSeeDetails={onSeeDetails}
        />
      )}
    </div>
  );
}
